import React, { useEffect } from "react";
import { ChatProvider, useChat, CHAT_MODES } from "./contexts/ChatContext";
import { useChatService } from "./services/ChatService";
import SidebarChat from "./SidebarChat";
import PopupChat from "./PopupChat";
import { useNewWindowChatManager } from "./services/NewWindowChatManager";

// Inner component that uses the chat context
const ChatManagerInner = ({
  sidebarOpen,
  onSidebarClose,
  conversationID,
  initialSuggestions,
  fileIds,
}) => {
  const chat = useChat();
  const newWindowManager = useNewWindowChatManager();
  const chatService = useChatService();

  // Update all new windows when messages change
  useEffect(() => {
    newWindowManager.updateAllWindows({
      messages: chat.messages,
      inputDraft: chat.inputDraft,
      loading: chat.loading,
      error: chat.error,
    });
  }, [
    chat.messages,
    chat.inputDraft,
    chat.loading,
    chat.error,
    newWindowManager,
  ]);

  // Cleanup all windows when component unmounts
  useEffect(() => {
    return () => {
      newWindowManager.closeAllWindows();
    };
  }, [newWindowManager]);

  // Handle chat mode changes for new window
  useEffect(() => {
    if (chat.mode === CHAT_MODES.NEWWINDOW) {
      const windowId = newWindowManager.createWindow(chatService, chat);
      if (!windowId) {
        // Failed to create window, fallback to popup mode
        chat.setError(
          "Failed to open new window. Please allow popups for this site."
        );
        setTimeout(() => chat.setError(null), 3000);
        chat.openPopup();
      } else {
        // Window created successfully, close chat in parent
        chat.closeChat();
      }
    }
  }, [chat.mode, newWindowManager, chat]);

  // Handle sidebar visibility - if sidebar should be open and we're not in popup/newwindow mode
  const shouldShowSidebar =
    sidebarOpen &&
    chat.mode !== CHAT_MODES.POPUP &&
    chat.mode !== CHAT_MODES.NEWWINDOW;

  // Show popup if mode is popup
  const shouldShowPopup = chat.mode === CHAT_MODES.POPUP;

  return (
    <>
      <SidebarChat
        open={shouldShowSidebar}
        onClose={onSidebarClose}
        conversationID={conversationID}
        initialSuggestions={initialSuggestions}
        fileIds={fileIds}
      />
      {shouldShowPopup && <PopupChat />}
    </>
  );
};

// Main component that provides the context
const ChatManager = ({
  sidebarOpen,
  onSidebarClose,
  conversationID,
  initialSuggestions = [],
  fileIds = null,
}) => {
  return (
    <ChatProvider>
      <ChatManagerInner
        sidebarOpen={sidebarOpen}
        onSidebarClose={onSidebarClose}
        conversationID={conversationID}
        initialSuggestions={initialSuggestions}
        fileIds={fileIds}
      />
    </ChatProvider>
  );
};

export default ChatManager;
